import { createContext, useContext, useCallback, useMemo, ReactNode } from 'react';
import { useClerkGate, useStableGetToken } from '@/hooks/useClerkGate';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOrganization } from './OrganizationContext';
import { useViewAs } from './ViewAsContext';
import { logError, logWarn } from '@/lib/logger';
import { API_BASE_URL } from '@/lib/api/config';
import { AccessApi } from '@/lib/api/domains/access.api';
import { useOrgAuthFetch } from '@/hooks/useOrgAuthFetch';
import { useAccessMe, type AccessViewer } from '@/hooks/useAccessMe';
import { managedAccessOf, type AccessState } from '@/lib/route-permissions';

// Import shared RBAC logic
import {
  canManageProjects,
  canCreateProject,
  canSeeBilling,
  canManageOrgStructure,
  canOperateIntegrations,
  canManageUsers,
  canListUsers,
  canUploadRecords,
  canManageAlerts,
  canEditProject as canEditProjectCore,
  canDeleteProject as canDeleteProjectCore,
  canViewProject as canViewProjectCore,
  type GlobalRole,
  type ProjectRole,
  type AuthContext as SharedAuthContext,
} from '@aica/core/rbac';

/**
 * Permission Context - Single Responsibility: Permission checks and authorization
 *
 * Now uses shared RBAC logic from @aica/core/rbac
 * This ensures frontend and backend use EXACTLY the same permission rules.
 */

// UUID validation
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isValidOrgId = (id: string | null | undefined): id is string => {
  if (!id || typeof id !== 'string') return false;
  return UUID_REGEX.test(id);
};

// Fetch project roles from API
async function fetchProjectRoles(
  token: string | null,
  orgId: string
): Promise<Map<string, ProjectRole>> {
  if (!token) {
    throw new Error('No authentication token available');
  }

  if (!isValidOrgId(orgId)) {
    throw new Error('Invalid organization ID format');
  }

  const rolesRes = await fetch(`${API_BASE_URL}/api/v1/projects/roles`, {
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Selected-Org-ID': orgId,
    },
  });

  if (!rolesRes.ok) {
    throw new Error(`Failed to load project roles: ${rolesRes.status}`);
  }

  const rolesData = (await rolesRes.json()) as { roles?: Record<string, ProjectRole> };
  const roles = rolesData.roles || {};
  const rolesMap = new Map<string, ProjectRole>();
  Object.entries(roles).forEach(([projectId, role]) => {
    rolesMap.set(projectId, role as ProjectRole);
  });

  return rolesMap;
}

export interface PermissionContextValue {
  globalRole: GlobalRole | null;
  projectRoles: Map<string, ProjectRole>;

  // True once `projectRoles` holds the caller's real `project_users` rows (a
  // settled `/projects/roles` success). Until then — loading, failed, or never
  // loaded (embedded) — the map is an EMPTY PLACEHOLDER, not "no projects":
  // a reader that says something off an empty map must wait for this.
  projectRolesLoaded: boolean;

  // Platform super-admin flag (can access admin panel, view all orgs)
  isOwner: boolean;

  /**
   * `/access/me` for the selected organization (E1.5, #1645): whether the
   * subject holds a grant and what it covers. Screens are decided from it by
   * `decideScreen` / `dashboardFor` (lib/route-permissions.ts), not from
   * `globalRole` alone.
   */
  access: AccessState;

  // True once `isOwner` carries a confirmed value from /me (settled success
  // or error), not just its unresolved `false` default. Consumers that make
  // a one-way decision off a *negative* `isOwner` (e.g. bouncing a non-owner
  // away from an owner-only view) should wait for this before acting — see
  // OrganizationContext's `isOwnerResolved` for why `isLoading` alone isn't
  // a safe enough gate.
  isOwnerResolved: boolean;

  // Effective role for UI rendering (viewAsRole || globalRole)
  // When owner uses "View As" mode, this returns the simulated role
  effectiveRole: GlobalRole | null;

  // Global permission checks (using shared RBAC)
  // These use effectiveRole when View As mode is active
  canAccess: (requiredRole: GlobalRole | GlobalRole[]) => boolean;
  canManageProjects: boolean;
  /**
   * Whether POST /projects will not refuse the subject: the role may create
   * (`canCreateProject` in `packages/core/src/rbac/permissions.ts`) and, for a
   * managed subject, the grant covers the whole organization (`org` scope).
   */
  canCreateProject: boolean;
  canSeeBilling: boolean;
  /**
   * Org structure (CRM import, department head assignment) — mirrors
   * `organization.manage_settings` (role `client` + platform owner only;
   * `admin` does not qualify, unlike `canManageUsers`). See
   * `canManageOrgStructure` in `packages/core/src/rbac/permissions.ts`.
   */
  canManageOrgStructure: boolean;
  /**
   * Integration operations (failed deliveries, health, the inbound log) —
   * mirrors `integration_operations.manage`: client, admin and the platform
   * owner. See `canOperateIntegrations` in `packages/core/src/rbac/permissions.ts`.
   */
  canOperateIntegrations: boolean;
  canManageUsers: boolean;
  /** Mirrors `user.list` (GET /users) — see `canListUsers` in @aica/core/rbac. */
  canListUsers: boolean;
  canUploadRecords: boolean;
  canManageAlerts: boolean;

  // Project-specific permissions (using shared RBAC)
  canEditProject: (projectId: string, projectOrgId: string) => boolean;
  canDeleteProject: (projectId: string, projectOrgId: string) => boolean;
  canViewProject: (projectId: string) => boolean;

  // Reserved for future use
  loadProjectRole: (projectId: string) => Promise<void>;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

// Module-level constant: an inline `= new Map()` destructuring default would
// create a FRESH Map identity on every render while the query has no data
// (loading/error/no org), defeating the value memoization below and
// re-rendering all consumers — the render amplification this provider's
// memoization exists to prevent.
// (Plain Map — the shared RBAC AuthContext type requires it; never mutated.)
const EMPTY_PROJECT_ROLES = new Map<string, ProjectRole>();

export function PermissionProvider({ children }: { children: ReactNode }) {
  const { currentOrganization, selectedOrgId, isOwner, isOwnerResolved } =
    useOrganization();
  const { getEffectiveRole } = useViewAs();
  // Stable Clerk access — see useClerkGate.ts (token-refresh render storm).
  const { isLoaded: isClerkLoaded, isSignedIn, userId: clerkUserId } = useClerkGate();
  const getToken = useStableGetToken();

  // Get global role from current organization
  // Platform owners get 'admin' role for all orgs from backend
  const globalRole = (currentOrganization?.role as GlobalRole) || null;

  // Effective role for UI rendering (viewAsRole || globalRole)
  // When owner uses "View As" mode, UI adapts to simulated role
  const effectiveRole = getEffectiveRole(globalRole);

  // Fetch project roles using TanStack Query
  const projectRolesQuery = useQuery<Map<string, ProjectRole>, Error>({
    queryKey: ['project-roles', selectedOrgId],
    queryFn: async () => {
      // Validate orgId before fetch - fail fast if not available
      if (!selectedOrgId) {
        throw new Error('Organization not selected');
      }
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token');
      }
      try {
        return await fetchProjectRoles(token, selectedOrgId);
      } catch (err) {
        logError(
          'Failed to load all project roles',
          { component: 'PermissionContext' },
          err instanceof Error ? err : undefined
        );
        throw err;
      }
    },
    enabled: Boolean(selectedOrgId && isClerkLoaded && isSignedIn),
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // Clean up old org data after 10 minutes
    refetchOnWindowFocus: false,
  });
  const projectRoles = projectRolesQuery.data ?? EMPTY_PROJECT_ROLES;
  const projectRolesLoaded = projectRolesQuery.isSuccess;

  // «My access» for the same organization as the project roles above.
  const { authenticatedFetch: orgAuthFetch } = useOrgAuthFetch(selectedOrgId);
  const accessApi = useMemo(() => new AccessApi(orgAuthFetch), [orgAuthFetch]);
  // The role and owner flag are part of what «my access» means: a change of
  // either closes what is open as a changed answer does (E1.5 PR 4b). Compared
  // only once `/me` settled, so its arrival is not a change.
  const accessViewer = useMemo(
    () => (isOwnerResolved ? { globalRole, isOwner } : null),
    [isOwnerResolved, globalRole, isOwner]
  );
  // The role comes from the `user-data` roster, which no access sweep touches
  // (it is identity): a role change by an admin leaves `/access/me` as it was.
  // On a growth of the revision the roster is re-read beside `/access/me`, and
  // the viewer is read off the fresh roster the way `OrganizationContext` reads
  // it (Codex P1, #1745). `null` — the roster could not be read.
  const queryClient = useQueryClient();
  const refreshViewer = useCallback(async (): Promise<AccessViewer | null> => {
    await queryClient.refetchQueries({ queryKey: ['user-data'], exact: true });
    const state = queryClient.getQueryState<{
      organizations: { id: string; role?: string | null }[];
      isOwner: boolean;
    }>(['user-data']);
    if (state?.status !== 'success' || !state.data) return null;
    const organization = state.data.organizations.find((o) => o.id === selectedOrgId);
    return {
      globalRole: (organization?.role as GlobalRole | undefined) || null,
      isOwner: state.data.isOwner,
    };
  }, [queryClient, selectedOrgId]);
  const access = useAccessMe({
    getMe: () => accessApi.getMe(),
    orgId: selectedOrgId,
    subjectId: clerkUserId,
    enabled: isClerkLoaded && isSignedIn,
    viewer: accessViewer,
    refreshViewer,
  });

  // Load single project role (reserved for future use)
  const loadProjectRole = useCallback(
    async (projectId: string) => {
      if (!selectedOrgId) return;

      try {
        const token = await getToken();
        if (!token) return;

        const res = await fetch(`${API_BASE_URL}/api/v1/projects/${projectId}/roles`, {
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Selected-Org-ID': selectedOrgId,
          },
        });

        if (!res.ok) {
          logWarn(`Failed to load project role for ${projectId}`, {
            component: 'PermissionContext',
            projectId,
          });
          return;
        }

        const data = (await res.json()) as { role?: ProjectRole };
        if (data.role) {
          // This is just a placeholder - in a real implementation,
          // we'd need to update the query cache or use a mutation
          logWarn(
            'loadProjectRole called but not fully implemented with TanStack Query',
            {
              component: 'PermissionContext',
              projectId,
            }
          );
        }
      } catch (err) {
        logError(
          `Failed to load project role for ${projectId}`,
          { component: 'PermissionContext', projectId },
          err instanceof Error ? err : undefined
        );
      }
    },
    [selectedOrgId, getToken]
  );

  // Create AuthContext for shared RBAC functions
  const authContext: SharedAuthContext | null = useMemo(() => {
    if (!selectedOrgId) return null;

    return {
      userId: selectedOrgId, // Using orgId as userId placeholder (frontend doesn't have internal userId)
      clerkUserId: selectedOrgId, // Using orgId as clerkUserId placeholder
      orgId: selectedOrgId,
      globalRole: globalRole, // Platform owners get 'admin' role for all orgs from backend
      isOwner, // Platform super-admin flag from /me endpoint
      projectRoles,
    };
  }, [globalRole, selectedOrgId, projectRoles, isOwner]);

  // Permission checks using shared RBAC
  // IMPORTANT: Use effectiveRole for UI visibility checks (respects View As mode)
  const canAccess = useCallback(
    (requiredRole: GlobalRole | GlobalRole[]) => {
      if (!effectiveRole) return false;
      const required = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
      return required.includes(effectiveRole);
    },
    [effectiveRole]
  );

  // Global permission checks (using shared RBAC helpers)
  // IMPORTANT: Use effectiveRole for UI visibility checks (respects View As mode)
  // isOwner flag grants full access — must be passed to all permission checks
  const canManageProjectsValue = useMemo(
    () => canManageProjects(effectiveRole, isOwner),
    [effectiveRole, isOwner]
  );
  // Separate from canManageProjects on purpose: that helper also gates the
  // billing-flavoured banners, and supervisors may create projects without
  // seeing tier advice.
  // A managed subject creates a project only with an `org` grant — a new
  // project is in no narrower grant's list, so POST /projects answers 403 to
  // `projects` and `departments` (`refuseProjectCreateOutsideGrant`, #2275).
  // While `/access/me` is unknown the role decides (`AccessState`).
  const canCreateProjectValue = useMemo(() => {
    if (!canCreateProject(effectiveRole, isOwner)) return false;
    const managed = managedAccessOf(access);
    return managed === null || managed.scope.kind === 'org';
  }, [effectiveRole, isOwner, access]);
  const canSeeBillingValue = useMemo(
    () => canSeeBilling(effectiveRole, isOwner),
    [effectiveRole, isOwner]
  );
  const canManageOrgStructureValue = useMemo(
    () => canManageOrgStructure(effectiveRole, isOwner),
    [effectiveRole, isOwner]
  );
  const canOperateIntegrationsValue = useMemo(
    () => canOperateIntegrations(effectiveRole, isOwner),
    [effectiveRole, isOwner]
  );
  const canManageUsersValue = useMemo(
    () => canManageUsers(effectiveRole, isOwner),
    [effectiveRole, isOwner]
  );
  const canListUsersValue = useMemo(
    () => canListUsers(effectiveRole, isOwner),
    [effectiveRole, isOwner]
  );
  const canUploadRecordsValue = useMemo(
    () => canUploadRecords(effectiveRole, isOwner),
    [effectiveRole, isOwner]
  );
  const canManageAlertsValue = useMemo(
    () => canManageAlerts(effectiveRole, isOwner),
    [effectiveRole, isOwner]
  );

  // Project-specific permissions (using shared RBAC helpers)
  const canEditProjectFn = useCallback(
    (projectId: string, projectOrgId: string) => {
      if (!authContext) return false;
      return canEditProjectCore(authContext, projectId, projectOrgId);
    },
    [authContext]
  );

  const canDeleteProjectFn = useCallback(
    (projectId: string, projectOrgId: string) => {
      if (!authContext) return false;
      return canDeleteProjectCore(authContext, projectId, projectOrgId);
    },
    [authContext]
  );

  const canViewProjectFn = useCallback(
    (projectId: string) => {
      if (!authContext) return false;
      return canViewProjectCore(authContext, projectId);
    },
    [authContext]
  );

  // Memoized so the context identity only changes when permissions actually
  // change — an inline object here re-rendered all consumers on every
  // provider render (token refresh, parent updates).
  const value = useMemo(
    () => ({
      globalRole,
      projectRoles,
      projectRolesLoaded,
      isOwner,
      access,
      isOwnerResolved,
      effectiveRole,
      canAccess,
      canManageProjects: canManageProjectsValue,
      canCreateProject: canCreateProjectValue,
      canSeeBilling: canSeeBillingValue,
      canManageOrgStructure: canManageOrgStructureValue,
      canOperateIntegrations: canOperateIntegrationsValue,
      canManageUsers: canManageUsersValue,
      canListUsers: canListUsersValue,
      canUploadRecords: canUploadRecordsValue,
      canManageAlerts: canManageAlertsValue,
      canEditProject: canEditProjectFn,
      canDeleteProject: canDeleteProjectFn,
      canViewProject: canViewProjectFn,
      loadProjectRole,
    }),
    [
      globalRole,
      projectRoles,
      projectRolesLoaded,
      isOwner,
      access,
      isOwnerResolved,
      effectiveRole,
      canAccess,
      canManageProjectsValue,
      canCreateProjectValue,
      canSeeBillingValue,
      canManageOrgStructureValue,
      canOperateIntegrationsValue,
      canManageUsersValue,
      canListUsersValue,
      canUploadRecordsValue,
      canManageAlertsValue,
      canEditProjectFn,
      canDeleteProjectFn,
      canViewProjectFn,
      loadProjectRole,
    ]
  );

  return (
    <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>
  );
}

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) throw new Error('usePermissions must be used within PermissionProvider');
  return context;
};

/**
 * The same context, or null outside PermissionProvider — for a component that
 * also renders in the Bitrix24 iframe. EmbeddedLayout mounts its own
 * permission context (EmbeddedRoleProvider), not this one, so `usePermissions`
 * would throw there and take the whole dashboard down (Codex P1 on #1425).
 */
export const useOptionalPermissions = () => useContext(PermissionContext);
